import { useMemo, useState } from 'react';
import type { MultiplierExplorerResult } from '../../analytics/multiplier-explorer.ts';
import { formatLocalStamp, formatMultiplier, formatUtcStamp } from '../../shared/format.ts';
import { fetchJson } from '../lib/api.ts';
import { StatusMessage } from '../components/StatusMessage.tsx';

const QUICK_THRESHOLDS = [100, 500, 1000, 5000, 10_000] as const;
const QUICK_TOP = [10, 20, 50] as const;

type ExplorerResponse = MultiplierExplorerResult & {
  dataLabel?: string;
  context?: Array<{
    sequenceNumber: number;
    multiplier: number;
    detectedAtUtc: string;
    segmentId: number;
    isEvent: boolean;
  }>;
};

export function MultiplierExplorerPage() {
  const [mode, setMode] = useState<'threshold' | 'top'>('threshold');
  const [thresholdInput, setThresholdInput] = useState('1000');
  const [customN, setCustomN] = useState('20');
  const [topN, setTopN] = useState(20);
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState<ExplorerResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);

  const threshold = Number(thresholdInput.replace(/[,xX>\s]/g, ''));

  async function run(next: { threshold?: number; topN?: number; offset?: number; contextSequence?: number }) {
    setBusy(true);
    setError(null);
    try {
      const payload = await fetchJson<ExplorerResponse>('/api/analytics/multipliers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          threshold: next.threshold,
          topN: next.topN,
          offset: next.offset ?? 0,
          limit: 100,
          contextSequence: next.contextSequence,
          contextBefore: 10,
          contextAfter: 10,
        }),
      });
      setResult(payload);
      setOffset(payload.offset);
      if (next.contextSequence != null) {
        setSelectedSeq(next.contextSequence);
      }
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Failed to load multipliers');
    } finally {
      setBusy(false);
    }
  }

  function applyThreshold(value = threshold) {
    if (!Number.isFinite(value) || value < 0) {
      setError('Enter a valid multiplier threshold.');
      return;
    }
    setMode('threshold');
    setSelectedSeq(null);
    void run({ threshold: value, offset: 0 });
  }

  function applyTop(n: number) {
    if (!Number.isFinite(n) || n < 1) {
      setError('Enter a valid Top N.');
      return;
    }
    setMode('top');
    setTopN(n);
    setSelectedSeq(null);
    void run({ topN: n, offset: 0 });
  }

  const pageCount = result ? Math.max(1, Math.ceil(result.totalMatches / result.limit)) : 1;
  const pageIndex = result ? Math.floor(result.offset / result.limit) + 1 : 1;
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-lg font-semibold">Multiplier Explorer</h1>
        <p className="mt-1 text-sm text-muted">Inspect extreme multipliers in the current Analytics snapshot.</p>
      </header>

      <section className="rounded border border-line bg-panel p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Threshold filter</h2>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="text-sm">
            Multiplier greater than
            <input
              className="ml-2 w-28 rounded border border-line bg-soft px-2 py-1"
              value={thresholdInput}
              onChange={(event) => setThresholdInput(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="rounded border border-accent bg-accent/20 px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            disabled={busy}
            onClick={() => applyThreshold()}
          >
            Apply
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {QUICK_THRESHOLDS.map((value) => (
            <button
              key={value}
              type="button"
              className="rounded border border-line px-2 py-1 text-xs font-semibold"
              onClick={() => {
                setThresholdInput(String(value));
                applyThreshold(value);
              }}
            >
              {value.toLocaleString('en-US')}x
            </button>
          ))}
        </div>
      </section>

      <section className="rounded border border-line bg-panel p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Top multipliers</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {QUICK_TOP.map((value) => (
            <button
              key={value}
              type="button"
              className="rounded border border-line px-2 py-1 text-xs font-semibold"
              onClick={() => applyTop(value)}
            >
              Top {value}
            </button>
          ))}
          <label className="text-sm">
            Custom N
            <input
              className="ml-2 w-20 rounded border border-line bg-soft px-2 py-1"
              value={customN}
              onChange={(event) => setCustomN(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="rounded border border-line px-2 py-1 text-xs font-semibold"
            disabled={busy}
            onClick={() => applyTop(Number.parseInt(customN, 10))}
          >
            Apply N
          </button>
        </div>
      </section>

      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      {busy && !result ? <StatusMessage kind="loading">Loading multipliers</StatusMessage> : null}

      {result ? (
        <>
          <section className="rounded border border-line bg-panel p-4 text-sm">
            {result.mode === 'threshold' ? (
              <p>
                Filter: &gt; {formatMultiplier(result.threshold)} · Matches: {result.totalMatches.toLocaleString('en-US')} ·
                Largest: {formatMultiplier(result.largest)} · First: {formatLocalStamp(result.firstOccurrenceUtc)} · Latest:{' '}
                {formatLocalStamp(result.latestOccurrenceUtc)}
              </p>
            ) : (
              <p>
                Top {result.topN} · Largest: {formatMultiplier(result.largest)} · {result.topN}th: {formatMultiplier(result.smallestInPage)}
              </p>
            )}
            <p className="mt-1 text-xs text-muted">Times shown in local timezone ({timezone}). UTC is in each row tooltip.</p>
          </section>

          <section className="rounded border border-line bg-panel">
            <div className="table-scroll">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-line text-xs text-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">Rank</th>
                    <th className="px-3 py-2 font-medium">Multiplier</th>
                    <th className="px-3 py-2 font-medium">Local</th>
                    <th className="px-3 py-2 font-medium">Sequence</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.length === 0 ? (
                    <tr>
                      <td className="px-3 py-4 text-muted" colSpan={4}>
                        No matching rounds.
                      </td>
                    </tr>
                  ) : (
                    result.rows.map((row) => (
                      <tr
                        key={row.sequenceNumber}
                        className="cursor-pointer border-t border-line hover:bg-soft"
                        title={`UTC ${formatUtcStamp(row.detectedAtUtc)}`}
                        onClick={() => {
                          const payload =
                            mode === 'threshold' ? { threshold, offset } : { topN, offset };
                          void run({ ...payload, contextSequence: row.sequenceNumber });
                        }}
                      >
                        <td className="px-3 py-2">{row.rank}</td>
                        <td className="px-3 py-2 font-medium">{formatMultiplier(row.multiplier)}</td>
                        <td className="px-3 py-2">{formatLocalStamp(row.detectedAtUtc)}</td>
                        <td className="px-3 py-2">seq {row.sequenceNumber}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {result.totalMatches > result.limit ? (
              <div className="flex items-center justify-between border-t border-line px-3 py-2 text-xs">
                <button
                  type="button"
                  className="rounded border border-line px-2 py-1 disabled:opacity-50"
                  disabled={busy || offset <= 0}
                  onClick={() =>
                    void run(
                      mode === 'threshold'
                        ? { threshold, offset: Math.max(0, offset - result.limit) }
                        : { topN, offset: Math.max(0, offset - result.limit) },
                    )
                  }
                >
                  Previous
                </button>
                <span>
                  Page {pageIndex} / {pageCount}
                </span>
                <button
                  type="button"
                  className="rounded border border-line px-2 py-1 disabled:opacity-50"
                  disabled={busy || offset + result.limit >= result.totalMatches}
                  onClick={() =>
                    void run(
                      mode === 'threshold'
                        ? { threshold, offset: offset + result.limit }
                        : { topN, offset: offset + result.limit },
                    )
                  }
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>
        </>
      ) : null}

      {result?.context && selectedSeq != null ? (
        <section className="rounded border border-line bg-panel p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Context around seq {selectedSeq}</h2>
          <ul className="mt-3 space-y-1 text-sm">
            {result.context.map((row) => (
              <li key={row.sequenceNumber} className={row.isEvent ? 'font-semibold' : 'text-muted'}>
                seq {row.sequenceNumber} · {formatMultiplier(row.multiplier)} · {formatLocalStamp(row.detectedAtUtc)}
                {row.isEvent ? ' · event' : ''}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
